/**
 * กระบี่ไผ่ห่มหมอก (breeze-sword): the caster's body through 颶 พายุป่าไผ่ (bs-tempest, the ท่าใหญ่), one motion per beat
 * of the rules' table (packages/shared/src/data/longMoves/breezeSword.ts `TEMPEST`, keys tp…). The caster becomes the
 * gust: planted, the sword ripped up out of the ground, three passes through the foe, a whirlwind leap above it, the
 * still gather at the apex, the dive, the landing on one knee, the flick and the stance. Research and the beat → motion
 * table: docs/research/TEMPEST_MOTION.md.
 *
 * Jian actions used (ideas only, our own choreography): 撩 rising arc (uproot, third pass), 扫 low sweep (first pass),
 * 抹 level slice at the neck (second pass), 云/旋 whirl (the leap), 劈 two-handed chop (the dive), the passing cut read as
 * in wuxia film editing (the launch and the held follow-through past the foe, the contact a single stretched key).
 * Each pass ends turned back to the foe (`view` 4), so every beat starts facing it.
 */
import type { Ease, LimbSpec } from '../kit';
import type { Pose } from '../index';
import { buildLong, type LongKey } from './kit';

/** The weapon hand and the blade's angle on screen in profile (−π/2 straight up, 0 forward, + down). */
export const ARMS = {
  /** Raised before the face, upright, both hands on the grip (the tell's lift). */
  tpUp: { hand: [7, 16], ang: -1.57, front: true },
  /** The point planted at the feet, both hands stacked on the pommel. */
  tpPlant: { hand: [11, 31], ang: 1.42, front: true },
  /** Point low and forward, about to be ripped up. */
  tpLowFwd: { hand: [10, 36], ang: 1.15, front: true },
  /** Mid-rip, the blade sweeping up level. */
  tpMidRise: { hand: [14, 27], ang: 0.15, front: true },
  /** Top of a rising arc, high and forward. */
  tpHighFwd: { hand: [12, 15], ang: -0.95, front: true },
  /** Overshot straight up. */
  tpSky: { hand: [9, 12], ang: -1.35, front: true },
  /** Drawn back low behind the hip, point trailing (the coil before a pass). */
  tpCoilLow: { hand: [-3, 35], ang: 2.7, front: false },
  /** Low sweep at the shins, level forward (first pass contact). */
  tpSweepLow: { hand: [15, 36], ang: 0.12, front: true },
  /** Swept through, trailing low behind (the passing cut's follow-through). */
  tpTrailLow: { hand: [-12, 31], ang: 3.0, front: false },
  /** Cocked over the back shoulder for a high slice. */
  tpCockHigh: { hand: [-4, 10], ang: -2.6, front: false },
  /** Coming over the shoulder, the blade leaning forward. */
  tpOver: { hand: [3, 10], ang: -2.0, front: true },
  /** Level slice at the neck (second pass contact). */
  tpSliceHigh: { hand: [16, 19], ang: -0.08, front: true },
  /** Swept through, trailing level behind at the shoulder. */
  tpTrailHigh: { hand: [-13, 21], ang: 3.05, front: false },
  /** Dropped low behind, point down and back, for a rising cut. */
  tpDropBack: { hand: [1, 36], ang: 2.1, front: true },
  /** Rising diagonal through the foe (third pass contact). */
  tpRiseCut: { hand: [14, 20], ang: -0.6, front: true },
  /** Upright before the body (turning with it). */
  tpUpright: { hand: [7, 22], ang: -1.5, front: true },
  /** Held out level to the side through the whirlwind. */
  tpSpinOut: { hand: [16, 23], ang: 0, front: true },
  /** Whirl cut high. */
  tpSpinHigh: { hand: [14, 15], ang: -0.7, front: true },
  /** Whirl cut low. */
  tpSpinLow: { hand: [15, 30], ang: 0.5, front: true },
  /** Both hands overhead, the point to heaven (the gather). */
  tpHeaven: { hand: [5, 8], ang: -1.57, front: true },
  /** Arched back over the head, wound for the chop. */
  tpArch: { hand: [0, 6], ang: -2.3, front: true },
  /** Two-handed chop halfway down. */
  tpChopMid: { hand: [14, 14], ang: -0.6, front: true },
  /** Two-handed chop through the foe. */
  tpChop: { hand: [15, 26], ang: 0.4, front: true },
  /** Driven down to the ground at the landing. */
  tpDriven: { hand: [15, 34], ang: 0.75, front: true },
  /** On the knee, swept out behind, level and low (展剑). */
  tpKneelOut: { hand: [-10, 33], ang: 3.0, front: false },
  /** The flick that shakes the blade clean. */
  tpFlick: { hand: [13, 33], ang: 0.8, front: true },
  /** The miss: tip dropped to the ground, the arm spent. */
  tpDragged: { hand: [9, 38], ang: 1.4, front: true },
} satisfies Record<string, LimbSpec>;

type Arm = keyof typeof ARMS;
/** The weapon hand at its arm's own place. */
const A = (a: Arm): [number, number] => ARMS[a].hand as [number, number];
/** Sword-fingers swept back level with the shoulder for balance (an open line against the blade). */
const FLUNG: [number, number] = [-13, 24];
/** Sword-fingers pointed at the foe. */
const POINT: [number, number] = [13, 24];

/** A key with the weapon arm and its hand. */
const k = (arm: Arm, key: Omit<LongKey, 'arm' | 'A'>): LongKey => ({ ...key, arm, A: A(arm) });

/** The tell (0.7 s, `opener.tellPose`): sword raised upright, driven point-down at the feet, both hands on the pommel;
 * the chest opens and qi lifts the robe and hair (they flutter on the hold). */
const PLANT: LongKey[] = [
  k('tpUp', { f: [-6, 4], o: 5, tilt: 6, far: 'fwd', B: [7, 17], face: 'block' }),
  k('tpUp', { f: [-7, 5], o: 2, tilt: -4, turn: -1, far: 'fwd', B: [7, 17], hem: 1 }),
  k('tpPlant', { feet: 1.5, f: [-9, 7], o: 8, tilt: 12, far: 'fwd', B: [10, 30], hem: 1, flare: 1 }),
  k('tpPlant', { feet: 1.6, f: [-9, 8], o: 8, tilt: 4, far: 'fwd', B: [10, 30], hem: 2, flare: 2, sway: -0.5, face: 'cast' }),
  k('tpPlant', { feet: 1.6, f: [-9, 8], o: 6, tilt: -2, far: 'fwd', B: [10, 30], hem: 3, flare: 3, sway: 0.8, face: 'cast' }),
  k('tpPlant', { feet: 1.6, f: [-9, 8], o: 6, tilt: -3, far: 'fwd', B: [10, 30], hem: 4, flare: 4, sway: -0.8, face: 'cast' }),
  k('tpPlant', { feet: 1.6, f: [-9, 8], o: 6, tilt: -2, far: 'fwd', B: [10, 30], hem: 4, flare: 4, sway: 0.8, face: 'cast' }),
  k('tpPlant', { feet: 1.6, f: [-9, 8], o: 7, tilt: -1, far: 'fwd', B: [10, 30], hem: 4, flare: 5, sway: -0.6 }),
];

/** tpUproot (beat 0, the catch): the blade ripped up out of the ground in a rising arc through the foe, up onto the toes,
 * the sword-fingers flung back; it settles and coils low for the first pass. */
const UPROOT: LongKey[] = [
  k('tpLowFwd', { feet: 1.5, f: [-9, 8], o: 10, lean: 1, tilt: 14, far: 'fwd', B: [7, 33], hem: 3, flare: 3 }),
  k('tpMidRise', { feet: 1.4, f: [-9, 9], o: 5, lean: 2, tilt: 4, turn: 2, far: 'back', B: [-10, 26], hem: 3, flare: 3, sway: -1 }),
  k('tpHighFwd', { feet: 1.3, f: [-8, 7], l: [2, 0], o: 0, lean: 1, tilt: -8, turn: 3, far: 'up', B: FLUNG, hem: 4, flare: 4, sway: -1.5 }),
  k('tpSky', { feet: 1.3, f: [-8, 7], l: [3, 0], o: -1, tilt: -10, turn: 2, far: 'up', B: FLUNG, hem: 4, flare: 3, sway: -1 }),
  k('tpHighFwd', { feet: 1.3, f: [-8, 7], o: 4, tilt: 0, far: 'back', B: [-13, 25], hem: 2, flare: 2, sway: 0.5 }),
  k('tpCoilLow', { feet: 1.4, f: [-10, 7], o: 9, lean: 2, tilt: 18, turn: -2, far: 'fwd', B: POINT, hem: 1, flare: 1, sway: 0.8 }),
  k('tpCoilLow', { feet: 1.4, f: [-10, 7], o: 10, lean: 2, tilt: 20, turn: -2, far: 'fwd', B: POINT, hem: 1, sway: 0.4 }),
];

/** tpPassA (first pass): exploding out of the coil low, the blade sweeps the shins as the body goes through, glides on
 * past trailing it low behind (held: the cut is read at its end), then whirls back to the foe and cocks it high. */
const PASS_A: LongKey[] = [
  k('tpMidRise', { feet: 1.1, f: [-14, 10], l: [5, 0], o: 6, lean: 3, tilt: 24, far: 'back', B: [-14, 24], sway: 2.5, hem: 2, flare: 2 }),
  k('tpSweepLow', { feet: 1.1, f: [-15, 12], l: [6, 2], o: 9, lean: 3, tilt: 30, far: 'back', B: [-15, 22], sway: 3, hem: 3, flare: 3 }),
  k('tpTrailLow', { feet: 1.1, f: [-13, 10], l: [5, 1], o: 9, lean: 3, tilt: 24, turn: 2, far: 'fwd', B: [13, 24], sway: 3, hem: 3, flare: 2 }),
  k('tpTrailLow', { feet: 1.2, f: [-12, 9], l: [3, 0], o: 9, lean: 2, tilt: 18, turn: 2, far: 'fwd', B: POINT, sway: 2.5, hem: 2, flare: 2 }),
  k('tpTrailLow', { feet: 1.4, f: [-10, 10], o: 11, lean: 1, tilt: 10, turn: 2, far: 'fwd', B: POINT, sway: 1, hem: 1, flare: 1 }),
  k('tpCockHigh', { feet: 1.3, f: [-9, 8], o: 9, tilt: 6, far: 'fwd', B: [13, 23], sway: -1, hem: 2, flare: 2, view: 2 }),
  k('tpCockHigh', { feet: 1.2, f: [-9, 7], o: 8, tilt: 8, turn: -2, far: 'fwd', B: [13, 23], sway: -0.5, hem: 1, view: 4 }),
];

/** tpPassB (second pass, back through): a running leap, the blade comes over the shoulder into a level slice at the
 * neck, trails high behind past the foe (held), then whirls back and drops low behind for the rising cut. */
const PASS_B: LongKey[] = [
  k('tpOver', { feet: 1.1, f: [-13, 9], l: [5, 2], o: 4, lean: 3, tilt: 18, far: 'fwd', B: [8, 24], sway: 2, hem: 2, flare: 2, view: 4 }),
  k('tpSliceHigh', { feet: 1.1, f: [-14, 11], l: [6, 3], o: 3, lean: 3, tilt: 14, turn: 3, far: 'back', B: [-13, 25], sway: 3, hem: 3, flare: 3, view: 4 }),
  k('tpTrailHigh', { feet: 1.1, f: [-13, 10], l: [5, 2], o: 4, lean: 2, tilt: 12, turn: 2, far: 'fwd', B: [13, 23], sway: 3, hem: 3, flare: 3, view: 4 }),
  k('tpTrailHigh', { feet: 1.2, f: [-12, 9], l: [4, 1], o: 5, lean: 2, tilt: 13, turn: 2, far: 'fwd', B: [13, 23], sway: 2.5, hem: 2, flare: 2, view: 4 }),
  k('tpTrailHigh', { feet: 1.4, f: [-10, 9], o: 9, lean: 1, tilt: 8, turn: 2, far: 'fwd', B: POINT, sway: 1, hem: 1, flare: 1, view: 4 }),
  k('tpDropBack', { feet: 1.3, f: [-9, 8], o: 9, tilt: 6, far: 'fwd', B: [9, 25], sway: -1, hem: 2, flare: 2, view: 6 }),
  k('tpDropBack', { feet: 1.3, f: [-10, 7], o: 10, lean: 1, tilt: 12, turn: -1, far: 'fwd', B: [9, 25], sway: -0.5, hem: 1, view: 8 }),
];

/** tpPassC (third pass, the triangle closes): driving low, the blade rises diagonally through the foe and lifts it,
 * finishing high over the head; past it the caster whirls back and sinks for the leap, arms swung down. */
const PASS_C: LongKey[] = [
  k('tpLowFwd', { feet: 1.1, f: [-14, 10], l: [5, 0], o: 7, lean: 3, tilt: 24, far: 'back', B: [-13, 24], sway: 2.5, hem: 2, flare: 2, view: 8 }),
  k('tpRiseCut', { feet: 1.1, f: [-14, 11], l: [6, 2], o: 3, lean: 3, tilt: 6, turn: 2, far: 'up', B: FLUNG, sway: 3, hem: 3, flare: 3, view: 8 }),
  k('tpHighFwd', { feet: 1.1, f: [-12, 9], l: [5, 2], o: 1, lean: 2, tilt: -6, turn: 2, far: 'up', B: FLUNG, sway: 2.5, hem: 4, flare: 3, view: 8 }),
  k('tpSky', { feet: 1.2, f: [-11, 9], l: [3, 0], o: 3, lean: 1, tilt: -2, far: 'up', B: FLUNG, sway: 2, hem: 3, flare: 2, view: 8 }),
  k('tpHighFwd', { feet: 1.4, f: [-10, 9], o: 8, tilt: 6, far: 'back', B: [-13, 25], sway: 1, hem: 2, flare: 1, view: 8 }),
  k('tpUpright', { feet: 1.3, f: [-8, 7], o: 8, tilt: 6, far: 'fwd', B: [6, 24], sway: -1, hem: 2, view: 10 }),
  k('tpCoilLow', { feet: 1.2, f: [-7, 6], o: 12, lean: 1, tilt: 14, far: 'back', B: [-6, 34], sway: -0.5, hem: 1, view: 12 }),
  k('tpCoilLow', { feet: 1.2, f: [-7, 6], o: 13, lean: 1, tilt: 16, far: 'back', B: [-6, 34], hem: 1, view: 12 }),
];

/** Leap height at the apex (art px): above the lifted foe (1.8 m at `LIFT_PX_PER_M` = 29 px) by about half a body, so
 * the caster reads over it (the rules' leap h 2.4 m is the sim's; the picture needs the gap). */
const TOP = 50;

/** tpRise (the storm): a whirlwind leap up beside the foe, two full turns in the air, the blade held out cutting four
 * times round (level, high, low, level), the knees drawn up; then drawn in, rising to the apex, both hands meeting
 * overhead. */
const RISE: LongKey[] = [
  k('tpHighFwd', { feet: 1, f: [-4, 4], l: [2, 4], o: 2, tilt: -4, far: 'up', B: FLUNG, sway: -2, hem: 3, flare: 2, lift: 8, view: 12 }),
  k('tpSpinOut', { feet: 1, f: [-3, 3], l: [8, 10], o: 3, tilt: 6, far: 'back', B: [-13, 25], sway: -2, hem: 4, flare: 4, lift: 21, view: 14 }),
  k('tpSpinOut', { feet: 1, f: [-3, 4], l: [9, 11], o: 2, tilt: 4, far: 'back', B: [-13, 25], sway: -2.5, hem: 4, flare: 4, lift: 32, view: 16 }),
  k('tpSpinHigh', { feet: 1, f: [-4, 4], l: [7, 10], o: 2, tilt: -2, far: 'back', B: [-13, 25], sway: -2, hem: 4, flare: 4, lift: 40, view: 18 }),
  k('tpSpinLow', { feet: 1, f: [-3, 3], l: [10, 9], o: 4, tilt: 10, far: 'back', B: [-13, 25], sway: -2.5, hem: 4, flare: 4, lift: 45, view: 20 }),
  k('tpSpinOut', { feet: 1, f: [-3, 4], l: [9, 11], o: 3, tilt: 4, far: 'back', B: [-13, 25], sway: -2, hem: 4, flare: 4, lift: 48, view: 22 }),
  k('tpUpright', { feet: 1, f: [-3, 3], l: [8, 9], o: 3, tilt: 2, far: 'fwd', B: [6, 22], sway: -1.5, hem: 4, flare: 3, lift: 49, view: 24 }),
  k('tpHeaven', { feet: 1, f: [-3, 4], l: [6, 9], o: 2, tilt: -3, far: 'up', B: [9, 23], sway: -1, hem: 4, flare: 3, lift: TOP, view: 28 }),
  k('tpHeaven', { feet: 1, f: [-3, 4], l: [4, 11], o: 1, tilt: -5, far: 'up', B: [9, 23], sway: -0.5, hem: 4, flare: 3, lift: TOP, view: 28 }),
];

/** tpGather (the quiet beat): hung at the apex on one leg, the knee drawn up, the point to heaven, every leaf streaming
 * to it; still but for the robes; then arched back like a drawn bow, the blade wound over the head. */
const GATHER: LongKey[] = [
  k('tpHeaven', { feet: 1, f: [-3, 4], l: [3, 12], o: 0, tilt: -6, far: 'up', B: [9, 23], sway: -1, hem: 4, flare: 3, face: 'cast', lift: TOP + 1, view: 28 }),
  k('tpHeaven', { feet: 1, f: [-3, 4], l: [3, 12], o: 0, tilt: -7, far: 'up', B: [9, 23], sway: 1, hem: 4, flare: 4, face: 'cast', lift: TOP + 2, view: 28 }),
  k('tpHeaven', { feet: 1, f: [-3, 4], l: [3, 12], o: 0, tilt: -7, far: 'up', B: [9, 23], sway: -1, hem: 4, flare: 4, face: 'cast', lift: TOP + 2, view: 28 }),
  k('tpHeaven', { feet: 1, f: [-3, 4], l: [3, 12], o: 1, tilt: -6, far: 'up', B: [9, 23], sway: 1, hem: 4, flare: 3, face: 'cast', lift: TOP + 3, view: 28 }),
  k('tpHeaven', { feet: 1, f: [-3, 4], l: [3, 12], o: 0, tilt: -8, far: 'up', B: [9, 23], sway: -0.5, hem: 4, flare: 4, lift: TOP + 3, view: 28 }),
  k('tpArch', { feet: 1, f: [-4, 4], l: [4, 10], o: 0, tilt: -11, far: 'up', B: [0, 6], sway: 0.5, hem: 4, flare: 3, lift: TOP + 2, view: 28 }),
  k('tpArch', { feet: 1, f: [-5, 4], l: [5, 8], o: 0, tilt: -13, far: 'up', B: [0, 6], sway: -0.5, hem: 4, flare: 3, lift: TOP + 1, view: 28 }),
];

/** tpDive (the finisher): pitched forward over the foe, the two-handed chop comes down through it, the robes blown up
 * by the fall, landing deep with the blade driven to the ground (impact on the last keys). */
const DIVE: LongKey[] = [
  k('tpHeaven', { feet: 1, f: [-4, 4], l: [6, 10], o: 2, tilt: 4, far: 'up', B: [9, 23], sway: -2, hem: 4, flare: 3, lift: TOP - 4, rot: 0.2, view: 28 }),
  k('tpChopMid', { feet: 1, f: [-5, 5], l: [7, 8], o: 3, tilt: 16, far: 'fwd', B: [13, 15], sway: -3, hem: 4, flare: 4, lift: 34, rot: 0.4, view: 28 }),
  k('tpChop', { feet: 1.1, f: [-6, 6], l: [6, 4], o: 4, tilt: 24, far: 'fwd', B: [14, 26], sway: -3, hem: 4, flare: 4, lift: 16, rot: 0.35, view: 28 }),
  k('tpDriven', { feet: 1.3, f: [-8, 9], l: [2, 0], o: 10, tilt: 26, far: 'fwd', B: [14, 33], sway: -1, hem: 3, flare: 3, lift: 3, rot: 0.1, view: 28 }),
  k('tpDriven', { feet: 1.4, f: [-9, 9], o: 15, lean: 1, tilt: 22, far: 'fwd', B: [14, 33], sway: 2, hem: 2, flare: 3, view: 28 }),
  k('tpDriven', { feet: 1.4, f: [-9, 9], o: 16, lean: 1, tilt: 24, far: 'fwd', B: [14, 33], sway: 1.5, hem: 1, flare: 2, view: 28 }),
  k('tpDriven', { feet: 1.4, f: [-9, 9], o: 16, lean: 1, tilt: 24, far: 'fwd', B: [14, 33], sway: 1, hem: 1, flare: 1, view: 28 }),
];

/** tpKneel: down on one knee, the blade swept out behind level and low, the sword-fingers at the foe, the head up;
 * held while the leaves rain. */
const KNEEL: LongKey[] = [
  k('tpDropBack', { feet: 1.3, f: [-8, 7], o: 16, tilt: 14, far: 'fwd', B: [10, 30], sway: 1, view: 28 }),
  k('tpKneelOut', { feet: 1.3, f: [-8, 7], o: 16, tilt: 8, far: 'fwd', B: [13, 25], sway: -1, hem: 1, view: 28 }),
  k('tpKneelOut', { feet: 1.3, f: [-8, 7], o: 16, tilt: 6, far: 'fwd', B: [13, 25], sway: 0.5, hem: 1, view: 28 }),
  k('tpKneelOut', { feet: 1.3, f: [-8, 7], o: 15, tilt: 5, far: 'fwd', B: [13, 25], sway: -0.3, view: 28 }),
  k('tpKneelOut', { feet: 1.3, f: [-8, 7], o: 16, tilt: 6, far: 'fwd', B: [13, 25], sway: 0.3, view: 28 }),
  k('tpKneelOut', { feet: 1.3, f: [-8, 7], o: 15, tilt: 5, far: 'fwd', B: [13, 25], sway: -0.2, view: 28 }),
  k('tpKneelOut', { feet: 1.3, f: [-8, 7], o: 16, tilt: 6, far: 'fwd', B: [13, 25], view: 28 }),
];

/** tpStance (the release): pushed up off the knee, the sword-fingers wiping the flat, a sharp flick that shakes the
 * blade clean, into the ready stance. */
const STANCE: LongKey[] = [
  k('tpKneelOut', { feet: 1.3, f: [-8, 6], o: 11, tilt: 10, far: 'fwd', B: [9, 29], view: 28 }),
  k('tpUpright', { feet: 1.2, f: [-7, 5], o: 6, tilt: 4, far: 'fwd', B: [8, 22], view: 28 }),
  k('tpUpright', { feet: 1.2, f: [-7, 5], o: 5, tilt: 2, far: 'fwd', B: [9, 19], view: 28 }),
  k('tpFlick', { feet: 1.2, f: [-6, 5], o: 4, tilt: 6, turn: 1, far: 'back', B: [-8, 26], view: 28 }),
  k('tpFlick', { feet: 1.2, f: [-6, 5], o: 4, tilt: 5, far: 'back', B: [-8, 26], view: 28 }),
  k('tpFlick', { feet: 1.2, f: [-6, 5], o: 3, tilt: 5, far: 'fwd', B: [9, 27], view: 28 }),
];

/** tpSpent (the whiff, 0.6 s + the 0.3 s stop): the rip cuts empty air, the body overbalances after it, the tip drops
 * to the ground, a hand on the knee, gasping (opened); then back toward the stance. */
const SPENT: LongKey[] = [
  k('tpLowFwd', { feet: 1.5, f: [-9, 8], o: 10, lean: 1, tilt: 14, far: 'fwd', B: [7, 33], hem: 3, flare: 3 }),
  k('tpHighFwd', { feet: 1.3, f: [-8, 7], l: [2, 0], o: 0, lean: 1, tilt: -8, turn: 3, far: 'up', B: FLUNG, hem: 4, flare: 4, sway: -1.5 }),
  k('tpSky', { feet: 1.3, f: [-8, 7], l: [3, 0], o: -1, tilt: -10, turn: 2, far: 'up', B: FLUNG, hem: 3, flare: 2 }),
  k('tpFlick', { feet: 1.3, f: [-8, 11], l: [0, 3], o: 6, lean: 3, tilt: 18, headDx: 2, far: 'back', B: [-12, 24], sway: 1.5, hem: 1 }),
  k('tpDragged', { feet: 1.4, f: [-8, 12], o: 10, lean: 2, tilt: 20, far: 'fwd', B: [8, 35], face: 'block', sway: 1 }),
  k('tpDragged', { feet: 1.4, f: [-8, 12], o: 11, lean: 2, tilt: 22, hunch: 2, far: 'fwd', B: [8, 35], face: 'block', closed: true }),
  k('tpDragged', { feet: 1.4, f: [-8, 12], o: 10, lean: 2, tilt: 21, hunch: 2, far: 'fwd', B: [8, 35], face: 'block' }),
  k('tpFlick', { feet: 1.3, f: [-7, 8], o: 7, lean: 1, tilt: 12, far: 'fwd', B: [8, 30], face: 'block' }),
];

/** The tempest's motions by the rules' pose keys. */
export type TempestMotionId =
  | 'tpPlant'
  | 'tpUproot'
  | 'tpPassA'
  | 'tpPassB'
  | 'tpPassC'
  | 'tpRise'
  | 'tpGather'
  | 'tpDive'
  | 'tpKneel'
  | 'tpStance'
  | 'tpSpent';
type N = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
export type Poses = `${TempestMotionId}_${N}`;

const built = buildLong<TempestMotionId, Poses>('พายุป่าไผ่', {
  tpPlant: { keys: PLANT, from: 'ready', ease: ['out', 'inOut', 'in', 'out', 'inOut', 'inOut', 'inOut', 'inOut'] },
  tpUproot: { keys: UPROOT, from: 'tpPlant_8', ease: ['in', 'in', 'out', 'out', 'inOut', 'inOut', 'inOut'] },
  tpPassA: { keys: PASS_A, from: 'tpUproot_7', ease: ['out', 'in', 'out', 'out', 'out', 'inOut', 'inOut'] },
  tpPassB: { keys: PASS_B, from: 'tpPassA_7', ease: ['out', 'in', 'out', 'out', 'out', 'inOut', 'inOut'] },
  tpPassC: { keys: PASS_C, from: 'tpPassB_7', ease: ['out', 'in', 'out', 'out', 'out', 'inOut', 'inOut', 'inOut'] },
  tpRise: { keys: RISE, from: 'tpPassC_8', ease: ['out', 'out', 'linear', 'linear', 'linear', 'linear', 'out', 'out', 'inOut'] },
  tpGather: { keys: GATHER, from: 'tpRise_9', ease: ['inOut', 'inOut', 'inOut', 'inOut', 'inOut', 'inOut', 'inOut'] },
  tpDive: { keys: DIVE, from: 'tpGather_7', ease: ['in', 'in', 'in', 'in', 'out', 'out', 'inOut'] },
  tpKneel: { keys: KNEEL, from: 'tpDive_7', ease: ['out', 'out', 'inOut', 'inOut', 'inOut', 'inOut', 'inOut'] },
  tpStance: { keys: STANCE, from: 'tpKneel_7', to: 'ready', ease: ['inOut', 'out', 'inOut', 'in', 'out', 'inOut', 'inOut'] },
  tpSpent: { keys: SPENT, from: 'tpPlant_8', to: 'ready', ease: ['in', 'out', 'out', 'out', 'out', 'inOut', 'inOut', 'inOut', 'inOut'] },
});

export const POSES = built.POSES;
export const MOTIONS = built.MOTIONS as unknown as readonly { id: TempestMotionId; keys: readonly Pose[]; ease: readonly Ease[] }[];
export const CHANNELS = built.CHANNELS;
